import React, {useContext, useEffect, useRef} from "react";
import {FocusTrap} from "focus-trap-react";
import IconButton from "@common/IconButton";
import {ChevronLeftIcon} from "@common/icons";
import {ErrorBoundary} from "react-error-boundary";
import ErrorFallback from "@common/ErrorFallback";
import Search from "./Search";
import PromptSearch from './PromptSearch';
import UserReview from "../review/UserReview";
import CMSSearch from "../cms/CMSSearch";
import useOnClickOutside from "@hooks/useOnClickOutside";
import {NavigationContext} from "@common/context/NavigationContext";
import ManageClassUsersDrawer from "../schoolManagement/drawerComponents/ManageClassUsersDrawer";
import {Close} from "@mui/icons-material";
import EditClassDrawer from "../schoolManagement/drawerComponents/EditClassDrawer";
import AddClassDrawer from "../schoolManagement/drawerComponents/AddClassDrawer";
import LessonPlannerReportDrawer from "../lessonPlanner/components/LessonPlannerReportDrawer";
import AddSectionDrawer from "../lessonPlanner/components/AddSectionDrawer";
import {useScreenMode} from "@common/context/ScreenModeContext";

const NavigationDrawer = props => {
    const {id, visible, setVisible, callback, componentName, triggerButtonRef} = props;
    const { drawerLocation, isReactDialogOpen } = useContext(NavigationContext);
    const menuRef = useRef();
    const closeButtonRef = useRef(null);
    const {mobileScreenMode} = useScreenMode();

    useOnClickOutside(menuRef, visible, () => {
        if (typeof callback === 'function') callback();
    });

    useEffect(() => {
        if (!visible || isReactDialogOpen) return;

        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                callback();

                if(document.activeElement) {
                    document.activeElement.blur();
                }
            }
        };

        window.addEventListener('keydown', onKeyDown);

        return () => {
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [visible, callback, isReactDialogOpen]);

    useEffect(() => {
        if (visible) {
            closeButtonRef.current?.focus();
        } else {
            triggerButtonRef?.current?.focus();
        }
    }, [visible, triggerButtonRef]);

    const drawerComponents = {
        Search: Search,
        PromptSearch: PromptSearch,
        UserReview: UserReview,
        CMSSearch: CMSSearch,
        ManageClassUsers: ManageClassUsersDrawer,
        EditClassDrawer: EditClassDrawer,
        AddClassDrawer: AddClassDrawer,
        LessonPlannerReportDrawer: LessonPlannerReportDrawer,
        AddSectionDrawer: AddSectionDrawer,
    }

    const drawerComponentsClasses = {
        Search: 'ch-nav-drawer-content',
        PromptSearch: 'ch-nav-drawer-content',
        UserReview: 'ch-nav-drawer-content',
        CMSSearch: 'ch-nav-drawer-content-large',
        ManageClassUsers: 'ch-nav-drawer-content-large',
        EditClassDrawer: 'ch-nav-drawer-content-large',
        AddClassDrawer: 'ch-nav-drawer-content-large',
        LessonPlannerReportDrawer: 'ch-nav-drawer-content-large',
        AddSectionDrawer: 'ch-nav-drawer-content-large',
    }

    const menuComponentsWidth = {
        Search: '840px',
        PromptSearch: '660px',
        UserReview: '660px',
        CMSSearch: '780px',
        ManageClassUsers: '650px',
        EditClassDrawer: '650px',
        AddClassDrawer: '650px',
        LessonPlannerReportDrawer: '480px',
        AddSectionDrawer: '660px',
    }

    const drawerComponent = drawerComponents[componentName];
    const drawerComponentClass = drawerComponentsClasses[componentName];
    const drawerComponentWidth = menuComponentsWidth[componentName];

    if(!visible) {
        return null;
    }

    const renderCloseButton = () => {
        return(
            <IconButton ref={closeButtonRef} label={componentName ? `Close ${componentName}` : 'Back to title navigation'} onClick={callback} size="large">                {drawerLocation === 'right'
                    ? <Close />
                    : <ChevronLeftIcon />
                }
            </IconButton>
        )
    }

    return (
        <FocusTrap active={visible && !isReactDialogOpen}
                   focusTrapOptions={{
                       returnFocusOnDeactivate: false
                   }}>
            <div id={id}
                 ref={menuRef}
                 className={'ch-nav-column ch-nav-drawer' + (visible ? ' ch-nav-drawer-visible' : ' ch-nav-drawer-hidden')}
                 role="dialog"
                 aria-labelledby="drawer-title"
                 aria-hidden={!visible}
                 tabIndex="-1">
                <div
                    className={`ch-nav-drawer-inner ch-nav-columns ${drawerLocation === 'right' ? 'ch-nav-drawer-right' : ''}`}
                    style={{width: drawerComponentWidth}}
                >
                    {!mobileScreenMode &&
                        renderCloseButton()
                    }
                    <div className={'ch-nav-column padding-y--m padding-r--m ' + drawerComponentClass}>
                        {mobileScreenMode &&
                            renderCloseButton()
                        }
                        {componentName && (
                            <ErrorBoundary
                                FallbackComponent={ErrorFallback}
                            >
                                {React.createElement(drawerComponent, {visible: visible, setVisible})}
                            </ErrorBoundary>
                        )}
                    </div>
                </div>
            </div>
        </FocusTrap>
    );
}

export default NavigationDrawer;